import 'bootstrap/dist/css/bootstrap.min.css'
import './App.css'
// CSS de todas as telas num arquivo só, na ordem de sempre (ver o arquivo)
import './estilosDasTelas'
import React from "react"
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom'

import moment from 'moment'
import 'moment/locale/pt-br'

// O que o App usa ao montar fica ESTÁTICO (no main.js): o login (1ª tela de
// quem chega sem sessão), a navbar, a gaveta de unidades, o aviso de
// assinatura e os planos. As TELAS das rotas vêm sob demanda (mais abaixo).
import Login from './components/Login'
import UnitNav from './components/Portal/UnitNav'

// SUBTABS COMPONENTS
import SideBarUnits from "./components/Tabs/SubTabs/SideBarUnits"

import AvisoAssinatura from './components/Assinatura/AvisoAssinatura'
import { temDireito, direitosDaSessao, salvarDireitosDaApi } from './components/Auxiliaries/planos'

// ICONS
import { RiRobot2Line } from "react-icons/ri"
import {
  IoGridOutline, IoHomeOutline, IoTrendingUpOutline, IoBarChartOutline, IoStarOutline,
  IoCloudUploadOutline, IoRocketOutline, IoMegaphoneOutline, IoNotificationsOutline, IoChatbubblesOutline,
  IoPeopleOutline, IoBanOutline, IoCopyOutline, IoWalletOutline, IoCarOutline, IoSettingsOutline,
  IoPersonAddOutline, IoGitNetworkOutline, IoDocumentTextOutline, IoSendOutline, IoCodeSlashOutline
} from "react-icons/io5"


// ── TELAS SOB DEMANDA ───────────────────────────────────────────────────────
// Cada tela é um arquivo JS próprio, baixado só quando a rota abre. Antes as
// ~50 telas iam juntas num main.js de 1 MB gzip — até para mostrar o login.
// O Admin Console é um grupo só ("admin"): quem é equipe baixa tudo de uma
// vez; a loja nunca baixa. Só o JS é dividido: o CSS das telas continua no
// main.css, na ordem de sempre (estilosDasTelas.js).
//
// Deploy novo com o painel aberto: os arquivos da versão antiga somem e a
// próxima tela não carrega (ChunkLoadError). Aí recarrega UMA vez para pegar
// o index.html novo; se falhar de novo em menos de 1 min (ou sem internet),
// o erro sobe para o <LimiteDeCarga>, que oferece recarregar.
const CHAVE_RECARGA = 'biwapp-recarga-tela'
const erroDeCarga = (erro) => !!erro && (erro.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(erro.message || '')))

function telaSobDemanda(carregar) {
  return React.lazy(() => carregar().catch((erro) => {
    if (!erroDeCarga(erro) || !navigator.onLine) throw erro
    let ultima = 0
    try { ultima = Number(sessionStorage.getItem(CHAVE_RECARGA)) || 0 } catch (e) { /* sem storage */ }
    if (Date.now() - ultima < 60 * 1000) throw erro
    try { sessionStorage.setItem(CHAVE_RECARGA, String(Date.now())) } catch (e) { /* sem storage */ }
    window.location.reload()
    return new Promise(() => { }) // segura o "carregando" até a página recarregar
  }))
}

// públicas (fora do login)
const ForgotPassword = telaSobDemanda(() => import(/* webpackChunkName: "senha" */ './components/ForgotPassword'))
const ResetPassword = telaSobDemanda(() => import(/* webpackChunkName: "senha" */ './components/ResetPassword'))
const Cadastro = telaSobDemanda(() => import(/* webpackChunkName: "cadastro" */ './components/Onboarding/Cadastro'))
const Onboarding = telaSobDemanda(() => import(/* webpackChunkName: "onboarding" */ './components/Onboarding/Onboarding'))

// loja — o Início é a tela depois do login: o navegador já o busca ocioso
// (prefetch) enquanto a pessoa digita a senha
const Inicio = telaSobDemanda(() => import(/* webpackChunkName: "inicio", webpackPrefetch: true */ './components/Portal/Inicio'))
const Resultados = telaSobDemanda(() => import(/* webpackChunkName: "resultados" */ './components/Portal/Resultados'))
const PainelBi = telaSobDemanda(() => import(/* webpackChunkName: "bi" */ './components/Portal/bi/PainelBi'))
const NotasAtendimento = telaSobDemanda(() => import(/* webpackChunkName: "notas" */ './components/Portal/notas/NotasAtendimento'))
const ImportarDados = telaSobDemanda(() => import(/* webpackChunkName: "importar" */ './components/Portal/importar/ImportarDados'))
// Clientes, Blacklist e Duplicados dividem a mesma base (dadosClientes): um arquivo só
const Clientes = telaSobDemanda(() => import(/* webpackChunkName: "clientes" */ './components/Portal/clientes/Clientes'))
const BlacklistClientes = telaSobDemanda(() => import(/* webpackChunkName: "clientes" */ './components/Portal/clientes/Blacklist'))
const Duplicados = telaSobDemanda(() => import(/* webpackChunkName: "clientes" */ './components/Portal/clientes/Duplicados'))
const Campanhas = telaSobDemanda(() => import(/* webpackChunkName: "campanhas" */ './components/Portal/campanhas/Campanhas'))
const QuemVoltou = telaSobDemanda(() => import(/* webpackChunkName: "quem-voltou" */ './components/Portal/conversao/QuemVoltou'))
const Conversas = telaSobDemanda(() => import(/* webpackChunkName: "conversas" */ './components/Portal/conversas/Conversas'))
const Mensageria = telaSobDemanda(() => import(/* webpackChunkName: "avisos" */ './components/Portal/mensageria/Mensageria'))
const Notificacoes = telaSobDemanda(() => import(/* webpackChunkName: "notificacoes" */ './components/Portal/notificacoes/Notificacoes'))
const Financeiro = telaSobDemanda(() => import(/* webpackChunkName: "financeiro" */ './components/Portal/financeiro/Financeiro'))
const Configuracoes = telaSobDemanda(() => import(/* webpackChunkName: "configuracoes" */ './components/Portal/configuracoes/Configuracoes'))
const Atendimento = telaSobDemanda(() => import(/* webpackChunkName: "atendimento" */ './components/Portal/atendimento/Atendimento'))
const Developers = telaSobDemanda(() => import(/* webpackChunkName: "developers" */ './components/Portal/developers/Developers'))
const Overview = telaSobDemanda(() => import(/* webpackChunkName: "pool" */ './components/Tabs/Overview'))
const MinhaAssinatura = telaSobDemanda(() => import(/* webpackChunkName: "assinatura" */ './components/Assinatura/MinhaAssinatura'))
const MeuPlano = telaSobDemanda(() => import(/* webpackChunkName: "plano" */ './components/Portal/plano/MeuPlano'))
const Upgrade = telaSobDemanda(() => import(/* webpackChunkName: "plano" */ './components/Portal/plano/Upgrade'))

// Admin Console — um grupo só
const CreateLogin = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/criar/CriarLogin'))
const CreatePool = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/criar/CriarPool'))
const ListLogins = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/usuarios/Usuarios'))
const ListLoginsInactive = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/usuarios/UsuariosInativos'))
const Dispatches = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/disparos/Disparos'))
const FinanceiroAdmin = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/financeiro/FinanceiroAdmin'))
const CreateNda = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/nda/Nda'))
const DevelopersAdmin = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/developers/DevelopersAdmin'))
const BiLogins = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/bi/BiGlobal'))
const AtendimentosAdmin = telaSobDemanda(() => import(/* webpackChunkName: "admin" */ './components/Portal/admin/atendimentos/Atendimentos'))

// Enquanto a tela baixa: nos primeiros 200 ms, nada (rede boa nem pisca);
// depois, o esqueleto do portal. A navbar fica fora do Suspense e não some.
function Carregando({ isDarkMode, comPainel }) {
  const [mostrar, setMostrar] = React.useState(false)
  React.useEffect(() => {
    const t = setTimeout(() => setMostrar(true), 200)
    return () => clearTimeout(t)
  }, [])
  if (!comPainel) {
    // login/cadastro/onboarding: sem navbar, só um giro discreto no centro
    return mostrar ? (
      <div role="status" aria-label="Carregando" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25d366' }}>
        <span className="onb-spinner" style={{ width: 30, height: 30 }} />
      </div>
    ) : null
  }
  return (
    <div className={'pt' + (isDarkMode ? ' dark' : '')} role="status" aria-label="Carregando" aria-busy="true">
      {mostrar && <div className="pt-main">
        <div className="pt-skel" style={{ height: 34, width: 280, maxWidth: '70%' }} />
        <div className="pt-skel" style={{ height: 180, marginTop: 22, borderRadius: 18 }} />
        <div className="pt-skel" style={{ height: 320, marginTop: 22, borderRadius: 18 }} />
      </div>}
    </div>
  )
}

// Tela que não baixou (ver telaSobDemanda): mensagem com "Recarregar" no
// lugar da tela, com a navbar funcionando. Outros erros seguem como antes.
class LimiteDeCargaBase extends React.Component {
  constructor(props) {
    super(props)
    this.state = { erro: null, local: props.local }
  }

  static getDerivedStateFromError(erro) {
    return { erro }
  }

  // trocou de rota: tenta a tela nova (a que falhou pode ser só aquela)
  static getDerivedStateFromProps(props, state) {
    if (state.erro && props.local !== state.local) return { erro: null, local: props.local }
    if (props.local !== state.local) return { local: props.local }
    return null
  }

  render() {
    const { erro } = this.state
    if (!erro) return this.props.children
    if (!erroDeCarga(erro)) throw erro
    return (
      <div className={'pt' + (this.props.isDarkMode ? ' dark' : '')}>
        <div className="pt-main">
          <div className="pt-card" style={{ padding: 28, maxWidth: 560 }}>
            <h3 style={{ fontSize: 18 }}>Não foi possível abrir esta tela</h3>
            <p style={{ marginTop: 8 }}>A conexão caiu ou o painel foi atualizado. Recarregue a página para continuar.</p>
            <button type="button" className="pt-btn primary" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>Recarregar</button>
          </div>
        </div>
      </div>
    )
  }
}

function LimiteDeCarga(props) {
  const location = useLocation()
  return <LimiteDeCargaBase {...props} local={location.pathname} />
}

// Depois do login, leva à tela certa pelo router (sem recarregar a página)
function IrAposLogin({ destino, aoChegar }) {
  const navigate = useNavigate()
  React.useEffect(() => {
    if (!destino) return
    navigate(destino.para, { replace: true })
    aoChegar()
  }, [destino, navigate, aoChegar])
  return null
}


// Rotas PÚBLICAS: além do login, o fluxo de "esqueci minha senha" — quem
// chega pelo link do e-mail não tem sessão e não pode ser jogado no /login.
const PUBLIC_PATHS = ['/login', '/esqueci-senha', '/redefinir-senha', '/cadastro']

// Redireciona para o login quem não está autenticado, EXCETO nas rotas
// públicas. Precisa ser um componente (useLocation) para reagir à navegação
// do react-router: o <Navigate> criado no render do App ficaria congelado.
function RedirectToLogin() {
  const location = useLocation()
  if (PUBLIC_PATHS.includes(location.pathname)) return null
  // link da campanha de volta (/assinatura?plano=…) sem sessão: guarda o plano para a
  // tela de reativar usar depois do login (só um id de plano — nada de redirecionamento)
  try {
    const plano = new URLSearchParams(location.search).get('plano')
    if (location.pathname === '/assinatura' && plano && /^[a-z_]{2,30}$/.test(plano)) sessionStorage.setItem('plano_volta', plano)
  } catch (e) { /* sem storage */ }
  return <Navigate to="/login" />
}

// Conta do cadastro self-serve que ainda não terminou o onboarding: o painel
// não existe para ela ainda (sem loja/plano). Qualquer rota leva de volta ao
// /onboarding — que retoma do passo em que o lead parou.
function OnboardingGate() {
  const location = useLocation()
  if (location.pathname === '/onboarding') return null
  return <Navigate to="/onboarding" replace />
}

// Assinatura self-serve suspensa (atraso) ou encerrada (cancelou): sem painel
// até regularizar/reativar — qualquer rota leva a /assinatura.
function BloqueioGate() {
  const location = useLocation()
  if (location.pathname === '/assinatura') return null
  return <Navigate to="/assinatura" replace />
}

// Troca de tela começa do topo: o app é uma página só e o navegador mantinha a
// rolagem da tela anterior (ex.: "Ver conversas" no pé do Início abria as
// Conversas já no meio). Com #âncora (ex.: /plano#planos) a própria tela rola.
function RolarParaOTopo() {
  const { pathname, hash } = useLocation()
  React.useLayoutEffect(() => {
    if (!hash) window.scrollTo(0, 0)
  }, [pathname, hash])
  return null
}

// Histórico de acessos (Developers → "🕓 Histórico de acessos"): a cada troca
// de rota registra a entrada na aba para a UNIDADE atual (resd). A API atribui
// quem navegou (unidade/pool/admin) pelo token — aqui só se informa a rota.
// Fire-and-forget: falha de rede não pode atrapalhar a navegação.
function ActivityTracker() {
  const location = useLocation()
  const lastSent = React.useRef('')
  React.useEffect(() => {
    const path = location.pathname
    const userId = localStorage.getItem('resd')
    const token = localStorage.getItem('nekb')
    if (!userId || !token || path === '/login' || path === '/onboarding') return
    // evita duplicar quando o efeito re-roda sem troca real de aba (StrictMode)
    const key = userId + '|' + path
    if (lastSent.current === key) return
    lastSent.current = key
    fetch(process.env.REACT_APP_BASE_URL + '/activity/tab', {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }),
      body: JSON.stringify({ userId, tab: path })
    }).catch(() => { })
  }, [location.pathname])
  return null
}

// Sessão: a API emite JWT com validade (12 h, campo `exp` do payload). A
// sessão do painel vale até esse `exp` — antes o front apagava tudo em 60 min
// (300 para equipe/pool) contados do login, mesmo com o token ainda válido.
// Token sem `exp` (emitido antes da validade): segue a regra antiga.
// Só LÊ o payload (base64url) — quem confere a assinatura é a API.
function expiraEmDoToken(token) {
  try {
    const parte = String(token || '').split('.')[1]
    if (!parte) return null
    const b64 = parte.replace(/-/g, '+').replace(/_/g, '/')
    const dados = JSON.parse(window.atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)))
    return typeof dados.exp === 'number' ? dados.exp * 1000 : null
  } catch (e) {
    return null
  }
}

// Preferências do aparelho, não da sessão: sobrevivem ao logout
const PREFERENCIAS_DO_APARELHO = ['login_theme', 'biwapp-guia-whatsapp-tema']
function limparSessao() {
  try {
    const guardadas = PREFERENCIAS_DO_APARELHO
      .map((k) => [k, localStorage.getItem(k)])
      .filter(([, v]) => v !== null)
    localStorage.clear()
    guardadas.forEach(([k, v]) => localStorage.setItem(k, v))
  } catch (e) { /* sem storage */ }
}

class App extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      isAuth: false,
      isMobile: (window.innerWidth < 700),
      hasBiTab: false,
      hasAiChat: false,
      isAdmin: false,
      isPool: false,
      viewType: 'user',
      authChecked: false,
      currentTabPath: localStorage.getItem("tabPath") || 'home',
      isDarkMode: localStorage.getItem('login_theme') === 'dark'
    }

    this.tokenDurationMinutes = (localStorage.getItem("biwapp_secret") === "VL101224" || localStorage.getItem("pool_secret") === "POOL_VL") ? 300 : 60
    this.sideBarAdminRef = React.createRef()
    this.sideBarUserRef = React.createRef()
  }

  componentDidMount() {
    this.checkIsAuth()
    this.checkLocalStorage()
    this.installAuthInterceptor()
    this.updateTabPathFromURL()

    const savedTheme = localStorage.getItem('login_theme')
    if (savedTheme) {
      this.setState({ isDarkMode: savedTheme === 'dark' })
      if (savedTheme === 'dark') {
        document.body.classList.add('dark-mode')
      }
    }

    // Listener para detectar mudanças de URL (botão voltar/avançar)
    window.addEventListener('popstate', this.updateTabPathFromURL)

    // plano da unidade: a equipe pode ter mudado desde o login (ou a sessão
    // é de antes desta versão) — confere com a API e redesenha a navbar
    window.addEventListener('biwapp:direitos', this.atualizarDireitos)
    this.conferirDireitosNaApi()
  }

  atualizarDireitos = () => {
    this.setState({ hasBiTab: temDireito('bi'), hasAiChat: temDireito('ia_notas'), dirVersao: Date.now() })
  }

  conferirDireitosNaApi = () => {
    const userId = localStorage.getItem('resd')
    const token = localStorage.getItem('nekb')
    if (!userId || !token || userId === 'biwapp_id' || localStorage.getItem('viewType') !== 'user') return
    fetch(process.env.REACT_APP_BASE_URL + '/planos/direitos/' + userId, { headers: { Authorization: 'Bearer ' + token } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j || !j.plano) return
        salvarDireitosDaApi(j.plano)
        try { localStorage.setItem('plano_nome', JSON.stringify({ resd: userId, nome: j.plano.nome })) } catch (e) { /* sem storage */ }
        this.atualizarDireitos()
      })
      .catch(() => { })
  }

  handleThemeToggle = () => {
    this.setState(prevState => {
      const nextIsDarkMode = !prevState.isDarkMode
      localStorage.setItem('login_theme', nextIsDarkMode ? 'dark' : 'light')
      if (nextIsDarkMode) {
        document.body.classList.add('dark-mode')
      } else {
        document.body.classList.remove('dark-mode')
      }
      return { isDarkMode: nextIsDarkMode }
    })
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.updateTabPathFromURL)
    window.removeEventListener('biwapp:direitos', this.atualizarDireitos)
    window.removeEventListener('nekb', this.aoEntrar)
  }

  // o token ainda vale? Pelo `exp` do JWT; sem ele, a regra antiga (minutos
  // desde o login, gravados em localStorage "exp")
  sessaoValida = () => {
    const token = localStorage.getItem("nekb")
    if (!token) return false
    const expiraEm = expiraEmDoToken(token)
    if (expiraEm !== null) return Date.now() < expiraEm
    if (localStorage.getItem("exp") === null) return false
    const tokenDuration = moment(new Date()).diff(moment(new Date(localStorage.getItem("exp"))), 'minutes')
    return tokenDuration < this.tokenDurationMinutes
  }

  checkIsAuth = () => {

    const isAdmin = localStorage.getItem("biwapp_secret") === "VL101224"
    const isPool = localStorage.getItem("pool_secret") === "POOL_VL"

    if (this.sessaoValida()) {
      this.setState({
        isAuth: true,
        hasBiTab: temDireito('bi'),
        hasAiChat: temDireito('ia_notas'),
        isAdmin: isAdmin ? true : false,
        isPool: isPool ? true : false,
        viewType: localStorage.getItem("viewType"),
        authChecked: true
      })
    } else {
      this.setState({
        isAuth: false,
        hasBiTab: false,
        hasAiChat: false,
        isAdmin: false,
        isPool: false,
        viewType: localStorage.getItem("viewType"),
        authChecked: true
      })
      // logout automático: mantém o tema escolhido
      limparSessao()
    }
  }

  checkLocalStorage = () => {
    window.addEventListener('nekb', this.aoEntrar)
  }

  // Login concluído: o Login.js gravou a sessão e disparou "nekb". A sessão
  // entra no estado e a troca de tela é do router — antes era um
  // location.href que baixava e executava o app inteiro de novo (~1 s no
  // celular) depois de um sleep(1000) fixo.
  aoEntrar = () => {
    const isAdmin = localStorage.getItem("biwapp_secret") === "VL101224"
    const isPool = localStorage.getItem("pool_secret") === "POOL_VL"
    const viewType = localStorage.getItem("viewType")

    let destino = "/"
    if (isAdmin) destino = "/listlogins"
    else if (viewType === "onboarding") destino = "/onboarding"
    else if (viewType === "bloqueado") destino = "/assinatura"
    else if (isPool) destino = "/overview"

    // Já houve outra sessão nesta página (saiu e entrou de novo, talvez com
    // outra conta): recarrega como antes, para nenhum dado guardado em
    // memória pela sessão anterior sobreviver.
    if (this.houveSessao) {
      window.location.href = destino
      return
    }

    // o tema pode ter sido trocado no próprio login
    const isDarkMode = localStorage.getItem('login_theme') === 'dark'
    document.body.classList.toggle('dark-mode', isDarkMode)
    this.tokenDurationMinutes = (isAdmin || isPool) ? 300 : 60

    this.setState({
      isAuth: true,
      hasBiTab: temDireito('bi'),
      hasAiChat: temDireito('ia_notas'),
      isAdmin: isAdmin ? true : false,
      isPool: isPool ? true : false,
      viewType,
      isDarkMode,
      authChecked: true,
      destinoAposLogin: { para: destino }
    })
    // o que o reload fazia no mount: conferir o plano da unidade na API
    this.conferirDireitosNaApi()
  }

  aoChegarAposLogin = () => {
    this.setState({ destinoAposLogin: null })
    this.updateTabPathFromURL()
  }

  // Sessão expirada NO MEIO do uso: o checkIsAuth só roda no mount, então o
  // token vencia com o app aberto e cada tela ficava presa em "Carregando..."
  // (todos os fetches respondiam 401/400 sem tratamento). Este interceptor
  // observa as respostas da API e, ao detectar token recusado, desloga e volta
  // para o login — em vez da tela travada.
  installAuthInterceptor = () => {
    if (window.__biwappAuthInterceptor) return
    window.__biwappAuthInterceptor = true

    const originalFetch = window.fetch.bind(window)
    window.fetch = async (...args) => {
      const response = await originalFetch(...args)

      if ((response.status === 401 || response.status === 400) && localStorage.getItem('nekb')) {
        try {
          const body = await response.clone().json()
          const message = body && body.message
          if (message === 'Token inválido!' || message === 'Acesso negado!') {
            // token morto (ex.: passou das 12 h): encerra a sessão como o
            // tryLogout faria, mantendo o tema, e leva direto ao login
            limparSessao()
            window.location.href = '/login'
            return response
          }
        } catch (e) { /* corpo não-JSON: segue normal */ }
      }
      return response
    }
  }

  tryLogout = () => {
    this.houveSessao = true // o próximo login desta página recarrega (ver aoEntrar)
    this.setState({
      isAuth: false,
      hasBiTab: false,
      hasAiChat: false,
      isAdmin: false,
      isPool: false,
      viewType: localStorage.getItem("viewType")
    })
    limparSessao()
  }

  // Loja do cadastro self-serve logada nela mesma (admin/pool navegando não
  // têm "Minha assinatura": a assinatura é da loja, não de quem olha)
  lojaSelfServe = () => localStorage.getItem('ss') === '1' && !this.state.isAdmin && !this.state.isPool

  tabSelector = (path) => {
    localStorage.setItem("tabPath", path)
    this.setState({ currentTabPath: path })
  }

  // ── NAVBAR DA UNIDADE ─────────────────────────────────────────────────────
  // Agrupada pelo que o dono da loja QUER FAZER, não pelo que o sistema faz:
  // "Números da loja" no lugar de "B.I.", "Não querem mensagens" no lugar de
  // "Blacklist". A definição fica AQUI, uma vez só, e alimenta o menu do
  // desktop e o do celular — separados, os dois divergiam a cada mudança.
  //
  // `on: false` tira o item (módulo não liberado no plano da unidade).
  // `solo` é o rótulo usado quando o grupo fica com um filho só e vira link
  // direto — abrir um menu para uma opção única é ruído.
  // Menu do Admin Console — mesma barra (UnitNav modo="admin") da unidade
  adminNavGroups = () => ([
    {
      title: 'Usuários',
      items: [
        { key: 'listlogins', to: '/listlogins', label: 'Unidades e logins', icon: IoPeopleOutline, desc: 'Todas as unidades: situação, plano, WhatsApp e importações' },
        { key: 'createlogin', to: '/createlogin', label: 'Criar login', divider: true, icon: IoPersonAddOutline, desc: 'Nova unidade de contrato' },
        { key: 'createpool', to: '/createpool', label: 'Criar pool', icon: IoGitNetworkOutline, desc: 'Um login que enxerga várias unidades' }
      ]
    },
    {
      title: 'Controles',
      items: [
        { key: 'bilogins', to: '/bilogins', label: 'B.I. Global', icon: IoBarChartOutline, desc: 'A BIWAPP em números: base, MRR, uso e risco' },
        { key: 'financeiro', to: '/financeiro', label: 'Financeiro', icon: IoWalletOutline, desc: 'Assinaturas, cobranças, cadastro online e cupons' },
        { key: 'dispatches', to: '/dispatches', label: 'Disparos', icon: IoSendOutline, desc: 'Campanhas, notificações e o gasto com a Meta' }
      ]
    },
    {
      title: 'Atendimentos',
      items: [
        { key: 'atendimentos', to: '/atendimentos', label: 'Atendimentos', solo: 'Atendimentos', icon: IoChatbubblesOutline, desc: 'A I.A. da BIWAPP atendendo os nossos clientes e leads' }
      ]
    },
    {
      title: 'NDA',
      items: [
        { key: 'createnda', to: '/createnda', label: 'NDA', solo: 'NDA', icon: IoDocumentTextOutline, desc: 'Acordos de confidencialidade na D4Sign' }
      ]
    },
    {
      title: 'Developers',
      items: [
        { key: 'developersadmin', to: '/developersadmin', label: 'Developers', solo: 'Developers', icon: IoCodeSlashOutline, desc: 'Saúde das APIs, radar da frota e acessos' }
      ]
    }
  ])

  // Itens fora do plano NÃO somem: viram vitrine (`locked` + `upgrade`) — o
  // selo "não contratado" leva à tela que explica o recurso e como liberar
  // (Portal/plano/Upgrade). O que cada plano libera: Auxiliaries/planos.
  unitNavGroups = () => {
    const tem = temDireito
    const fora = (chave, rota) => (tem(chave) ? {} : { locked: true, upgrade: rota || '/upgrade/' + chave })
    const atende = tem('ia_atendimento')
    return ([
      {
        // Início é o resumo da conta; fica sozinho, primeiro, como link direto
        title: 'Início',
        items: [
          { key: 'home', to: '/', label: 'Início', solo: 'Início', icon: IoHomeOutline, desc: 'Resumo da loja, alertas e atalhos' }
        ]
      },
      {
        title: 'Desempenho',
        items: [
          { key: 'resultados', to: '/resultados', label: 'Resultados das campanhas', icon: IoTrendingUpOutline, desc: 'Quantos clientes voltaram e quanto venderam',
            match: ['/resultados', '/clientsbirthday', '/clientsmissyou', '/clientsperpetuity'], ...fora('campanhas') },
          { key: 'bi', to: '/bi', label: 'Painel B.I.', icon: IoBarChartOutline, desc: 'Clientes ativos, ticket e receita mês a mês', ...fora('bi') },
          { key: 'aichatsreports', to: '/aichatsreports', label: 'Notas do atendimento', icon: IoStarOutline, desc: 'A nota diária que a I.A. dá para a equipe', ...fora('ia_notas') },
          // Importar dados fecha o grupo, atrás de um divisor: é a AÇÃO que
          // alimenta os números acima — mora perto deles, mas separada das
          // telas de leitura. Só aparece em plano com campanhas/B.I. (nos
          // planos só de I.A. a planilha não alimenta nada); a rota continua.
          { key: 'importdata', to: '/importdata', label: 'Importar dados', divider: true, icon: IoCloudUploadOutline, desc: 'Envie as planilhas do caixa do mês', on: tem('campanhas') || tem('bi') }
        ]
      },
      {
        title: 'Mensagens',
        items: [
          { key: 'campaigns', to: '/campaigns', label: 'Campanhas', icon: IoRocketOutline, desc: 'Aniversário e “sentimos sua falta”, automáticas', ...fora('campanhas') },
          { key: 'messaging', to: '/messaging', label: 'Avisos', icon: IoMegaphoneOutline, desc: 'Promoções e comunicados para os clientes', ...fora('avisos') },
          { key: 'notifications', to: '/notifications', label: 'Notificações', icon: IoNotificationsOutline, desc: 'Mensagens automáticas do caixa', ...fora('notificacoes') },
          { key: 'conversas', to: '/aichatsmessages/messages?period=last7', label: 'Conversas do WhatsApp', icon: IoChatbubblesOutline, desc: 'O que os clientes falaram com a loja',
            match: ['/aichatsmessages'], ...fora('ia_notas') }
        ]
      },
      {
        title: 'Clientes',
        items: [
          { key: 'clients', to: '/clients', label: 'Lista de clientes', solo: 'Clientes', icon: IoPeopleOutline, desc: 'Toda a base, com busca' },
          { key: 'blacklist', to: '/blacklist', label: 'Não querem mensagens', icon: IoBanOutline, desc: 'Quem pediu para não receber mensagens' },
          { key: 'duplicated', to: '/duplicated', label: 'Duplicados', icon: IoCopyOutline, desc: 'Cadastros repetidos para revisar' }
        ]
      },
      {
        // Módulos contratáveis além do plano base — o grupo é a VITRINE:
        // `soon` = ainda não lançado (selo "em breve");
        // `locked` = lançado mas não habilitado para esta unidade (selo "não
        // contratado", leva ao upgrade). Habilitou, vira a tela.
        title: 'Módulos',
        items: [
          { key: 'financeiro', to: '/financeiro', label: 'Financeiro', icon: IoWalletOutline, desc: 'Vendas do dia e projeção do mês', ...fora('financeiro') },
          { key: 'deliveryapp', to: '/', label: 'Delivery App', soon: true, upgrade: '/upgrade/delivery', icon: IoCarOutline, desc: 'Coletas e entregas roteirizadas' }
        ]
      },
      {
        title: 'Configurações',
        items: [
          { key: 'configs', to: '/configs', label: 'Geral', solo: 'Configurações', icon: IoSettingsOutline, desc: 'Loja, WhatsApp e acesso' },
          // /aichat é a tela de CONFIGURAÇÃO do robô (horários, prompt, número),
          // não o histórico de conversas — por isso mora aqui, não em Mensagens.
          // Gerente I.A.: a tela existe (relatórios), mas o robô não responde.
          { key: 'aichat', to: '/aichat', label: atende || !tem('ia_notas') ? 'Atendimento I.A. (ajustes)' : 'Gerente I.A. (ajustes)', icon: RiRobot2Line,
            desc: atende || !tem('ia_notas') ? 'Horários, preços e jeito de responder' : 'Relatórios e o que a I.A. acompanha', ...fora('ia_notas', '/upgrade/ia_atendimento') }
        ]
      }
    ])
  }

  // Nome do plano na barra: o do resumo (Início grava para a unidade atual)
  // ou, antes dele carregar, o rótulo pelo código do plano.
  nomeDoPlano = () => {
    try {
      const salvo = JSON.parse(localStorage.getItem('plano_nome') || 'null')
      if (salvo && salvo.resd === localStorage.getItem('resd') && salvo.nome) return salvo.nome
    } catch (e) { /* valor antigo/inválido: usa o código */ }
    return direitosDaSessao().nome || null
  }

  // Admin Console: volta a sessão para a visão de administrador
  irParaAdminConsole = () => {
    localStorage.setItem("resd", "biwapp_id")
    localStorage.setItem("username", "@biwapp.com.br")
    localStorage.setItem("tabPath", "listlogins")
    localStorage.setItem("viewType", "admin")
    localStorage.removeItem("nim")
    localStorage.removeItem("xam")
    localStorage.removeItem("act")
    localStorage.removeItem("pdv")
    localStorage.removeItem("mod_fin")
    localStorage.removeItem("mod_del")
    localStorage.removeItem("mod_not")
    localStorage.removeItem("ddd")
    localStorage.removeItem("place_name")
    localStorage.removeItem("camp")
    localStorage.removeItem("nalp")
    localStorage.removeItem("dir")
    localStorage.removeItem("TAG")
    localStorage.removeItem("plusCamp3")
    localStorage.removeItem("jumpClients")
    window.location.href = "/listlogins"
  }

  updateTabPathFromURL = () => {
    const currentPath = window.location.pathname;
    let tabPath = null;

    // Mapeamento de rotas para tabPath
    if (currentPath === '/') {
      tabPath = 'home';
    } else if (currentPath === '/resultados') {
      tabPath = 'resultados';
    } else if (currentPath === '/campaigns') {
      tabPath = 'campaigns';
    } else if (currentPath === '/messaging') {
      tabPath = 'messaging';
    } else if (currentPath === '/notifications') {
      tabPath = 'notifications';
    } else if (currentPath === '/importdata') {
      tabPath = 'importdata';
    } else if (currentPath === '/financeiro') {
      tabPath = 'financeiro';
    } else if (currentPath === '/bi') {
      tabPath = 'bi';
    } else if (currentPath.includes('/aichatsreports')) {
      // relatórios de nota entram em Desempenho; as conversas ficam em Mensagens
      tabPath = 'aichatsreports';
    } else if (currentPath.includes('/aichatsmessages')) {
      tabPath = 'conversas';
    } else if (currentPath === '/aichat') {
      tabPath = 'aichat';
    } else if (currentPath === '/configs') {
      tabPath = 'configs';
    } else if (currentPath === '/listlogins' || currentPath === '/listloginsinactive' || currentPath === '/createlogin' || currentPath === '/createpool' || currentPath === '/createnda') {
      tabPath = 'listlogins';
    } else if (currentPath === '/overview') {
      tabPath = 'overview';
    } else if (currentPath === '/clients') {
      tabPath = 'clients';
    } else if (currentPath === '/blacklist') {
      tabPath = 'blacklist';
    } else if (currentPath === '/duplicated') {
      tabPath = 'duplicated';
    } else if (currentPath === '/bilogins') {
      tabPath = 'bilogins';
    } else if (currentPath === '/dispatches') {
      tabPath = 'dispatches';
    } else if (currentPath === '/developersadmin') {
      tabPath = 'developersadmin';
    } else if (currentPath === '/atendimentos') {
      tabPath = 'atendimentos';
    } else if (currentPath === '/developers') {
      tabPath = 'developers';
    } else {
      // Default para home se não encontrar
      tabPath = 'home';
    }

    if (tabPath) {
      localStorage.setItem("tabPath", tabPath);
      // Atualiza o estado para forçar re-render da navbar
      this.setState({ currentTabPath: tabPath });
    }
  }


  render() {
    // O que o plano da unidade libera (Auxiliaries/planos — a API trava de
    // verdade). Fora do plano, a rota mostra a vitrine de upgrade no lugar da
    // tela. Financeiro é módulo (Configurações → Plano e módulos).
    const tem = temDireito
    const up = (recurso) => <Upgrade recurso={recurso} isDarkMode={this.state.isDarkMode} donoDaLoja={!this.state.isAdmin && !this.state.isPool} isAdmin={this.state.isAdmin} />
    const hasFinanceTab = tem('financeiro')
    return (
      <div className={`App ${this.state.isDarkMode ? 'dark-mode' : ''}`} style={{ zoom: '0.8' }} >

        <BrowserRouter>

          <RolarParaOTopo />
          {this.state.isAuth && <ActivityTracker />}

          {this.state.isAuth && this.state.isAdmin && this.state.viewType === 'admin' &&
            <>
              <UnitNav
                modo="admin"
                grupos={this.adminNavGroups().map((grupo) => ({ ...grupo, itens: grupo.items }))}
                isMobile={this.state.isMobile}
                isDarkMode={this.state.isDarkMode}
                onToggleTheme={this.handleThemeToggle}
                onLogout={this.tryLogout}
                onSelect={this.tabSelector}
                onTrocarUnidade={() => { this.sideBarAdminRef.current && this.sideBarAdminRef.current.open() }}
                isAdmin
              />
              {/* lista de unidades: só a gaveta; o botão é o "Entrar em uma unidade" da conta */}
              <span style={{ display: 'none' }}><SideBarUnits ref={this.sideBarAdminRef} execute={"true"} /></span>
            </>
          }

          {this.state.isAuth && (!this.state.isAdmin || this.state.viewType === 'user') && this.state.viewType !== 'pool' && this.state.viewType !== 'onboarding' && this.state.viewType !== 'bloqueado' &&
            <>
              <UnitNav
                grupos={this.unitNavGroups()
                  .map((grupo) => ({ ...grupo, itens: grupo.items.filter((i) => i.on !== false) }))
                  .filter((grupo) => grupo.itens.length > 0)}
                isMobile={this.state.isMobile}
                isDarkMode={this.state.isDarkMode}
                onToggleTheme={this.handleThemeToggle}
                onLogout={this.tryLogout}
                onSelect={this.tabSelector}
                plano={this.nomeDoPlano()}
                lojaSelfServe={this.lojaSelfServe()}
                podeTrocarUnidade={this.state.isAdmin || this.state.isPool || localStorage.getItem("username") === 'virginia'}
                onTrocarUnidade={() => { this.sideBarUserRef.current && this.sideBarUserRef.current.open() }}
                isAdmin={this.state.isAdmin}
                onAdminConsole={this.irParaAdminConsole}
              />
              {/* lista de unidades (admin/pool): só a gaveta; o botão é o "Trocar de unidade" da navbar */}
              <span style={{ display: 'none' }}><SideBarUnits ref={this.sideBarUserRef} execute={"true"} /></span>
            </>
          }

          {this.state.isAuth && this.state.isPool && this.state.viewType === 'pool' &&
            <>
              <UnitNav
                modo="pool"
                grupos={[{ title: 'Visão geral', itens: [{ key: 'overview', label: 'Visão geral', solo: 'Visão geral', to: '/overview', icon: IoGridOutline, desc: 'Todas as unidades do seu pool' }] }]}
                isMobile={this.state.isMobile}
                isDarkMode={this.state.isDarkMode}
                onToggleTheme={this.handleThemeToggle}
                onLogout={this.tryLogout}
                onSelect={this.tabSelector}
                onTrocarUnidade={() => { this.sideBarUserRef.current && this.sideBarUserRef.current.open() }}
              />
              {/* lista de unidades do pool: só a gaveta; o botão é o "Entrar em uma unidade" da conta */}
              <span style={{ display: 'none' }}><SideBarUnits ref={this.sideBarUserRef} execute={"true"} /></span>
            </>
          }

          {this.state.authChecked && !this.state.isAuth && <RedirectToLogin />}
          {this.state.isAuth && this.state.viewType === 'onboarding' && <OnboardingGate />}
          {this.state.isAuth && this.state.viewType === 'bloqueado' && <BloqueioGate />}
          {this.state.isAuth && this.lojaSelfServe() && this.state.viewType === 'user' && <AvisoAssinatura />}

          {/* só depois do checkIsAuth: no 1º render viewType/isAdmin ainda são os
              padrões ('user'/false) e a rota da loja montava por engano (ex.: F5 em
              /financeiro do Admin Console pedia /planos/direitos do admin → 404) */}
          <IrAposLogin destino={this.state.destinoAposLogin} aoChegar={this.aoChegarAposLogin} />

          {this.state.authChecked && <LimiteDeCarga isDarkMode={this.state.isDarkMode}>
          <React.Suspense fallback={<Carregando isDarkMode={this.state.isDarkMode} comPainel={this.state.isAuth && this.state.viewType !== 'onboarding' && this.state.viewType !== 'bloqueado'} />}>
          <Routes>
            <Route path='/login' index element={<Login />}></Route>
            <Route path='/esqueci-senha' index element={<ForgotPassword />}></Route>
            <Route path='/redefinir-senha' index element={<ResetPassword />}></Route>
            <Route path='/cadastro' index element={<Cadastro />}></Route>
            <Route path='/onboarding' index element={<Onboarding />}></Route>
            <Route path='/assinatura' index element={<MinhaAssinatura bloqueado={this.state.viewType === 'bloqueado'} isDarkMode={this.state.isDarkMode} />}></Route>
            <Route path='/plano' index element={<MeuPlano isDarkMode={this.state.isDarkMode} donoDaLoja={!this.state.isAdmin && !this.state.isPool} />}></Route>
            <Route path='/' index element={<Inicio isDarkMode={this.state.isDarkMode} />}></Route>
            <Route path='/resultados' index element={tem('campanhas') ? <Resultados isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            <Route path='/campaigns' index element={tem('campanhas') ? <Campanhas isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            <Route path='/messaging' index element={tem('avisos') ? <Mensageria isDarkMode={this.state.isDarkMode} /> : up('avisos')}></Route>
            <Route path='/notifications' index element={tem('notificacoes') ? <Notificacoes isDarkMode={this.state.isDarkMode} /> : up('notificacoes')}></Route>
            <Route path='/clients' index element={<Clientes isDarkMode={this.state.isDarkMode} />}></Route>
            {/* fora do plano (sem campanhas e sem B.I.) a planilha não alimenta nada:
                antes a tela ficava em "preparando a sua loja" para sempre */}
            <Route path='/importdata' index element={tem('campanhas') || tem('bi') ? <ImportarDados isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            {/* /financeiro existe nos dois lados: a conferência da unidade e o
                Financeiro do Admin Console. No console vale sempre o do admin
                (mod_fin de uma unidade visitada antes não pode sequestrar a rota). */}
            {this.state.viewType !== 'admin' &&
              <Route path='/financeiro' index element={hasFinanceTab ? <Financeiro isDarkMode={this.state.isDarkMode} /> : up('financeiro')}></Route>
            }
            <Route path='/bi' index element={tem('bi') ? <PainelBi isDarkMode={this.state.isDarkMode} /> : up('bi')}></Route>
            {/* /aichat: Gerente I.A. também entra (relatórios); o robô não responde (trava no servidor) */}
            <Route path='/aichat' index element={tem('ia_notas') ? <Atendimento isDarkMode={this.state.isDarkMode} /> : up('ia_atendimento')}></Route>
            {/* vitrine de um recurso fora do plano (navbar "não contratado", atalhos do Início) */}
            <Route path='/upgrade/:recurso' index element={<Upgrade isDarkMode={this.state.isDarkMode} donoDaLoja={!this.state.isAdmin && !this.state.isPool} isAdmin={this.state.isAdmin} />}></Route>
            <Route path='/configs' index element={<Configuracoes isDarkMode={this.state.isDarkMode} />}></Route>
            <Route path='/overview' index element={<Overview isDarkMode={this.state.isDarkMode} />}></Route>
            {this.state.isAdmin &&
              <>
                <Route path='/createlogin' index element={<CreateLogin isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/createpool' index element={<CreatePool isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/listlogins' index element={<ListLogins isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/listloginsinactive' index element={<ListLoginsInactive isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/createnda' index element={<CreateNda isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/bilogins' index element={<BiLogins isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/dispatches' index element={<Dispatches isDarkMode={this.state.isDarkMode} />}></Route>
                {this.state.viewType === 'admin' && <Route path='/financeiro' index element={<FinanceiroAdmin isDarkMode={this.state.isDarkMode} />}></Route>}
                <Route path='/developersadmin' index element={<DevelopersAdmin isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/atendimentos' index element={<AtendimentosAdmin isDarkMode={this.state.isDarkMode} />}></Route>
                <Route path='/developers' index element={<Developers isDarkMode={this.state.isDarkMode} isAdmin={this.state.isAdmin} />}></Route>
              </>
            }
            <Route path='/clientsbirthday/:id' index element={tem('campanhas') ? <QuemVoltou key='aniversario' tipo='aniversario' isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            <Route path='/clientsmissyou/:id' index element={tem('campanhas') ? <QuemVoltou key='sentimos' tipo='sentimos' isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            <Route path='/clientsperpetuity/:id' index element={tem('campanhas') ? <QuemVoltou key='perpetuidade' tipo='perpetuidade' isDarkMode={this.state.isDarkMode} /> : up('campanhas')}></Route>
            <Route path='/blacklist' index element={<BlacklistClientes isDarkMode={this.state.isDarkMode} />}></Route>
            <Route path='/duplicated' index element={<Duplicados isDarkMode={this.state.isDarkMode} />}></Route>
            <Route path='/aichatsmessages/:id' index element={tem('ia_notas') ? <Conversas isDarkMode={this.state.isDarkMode} /> : up('ia_notas')}></Route>
            <Route path='/aichatsreports' index element={tem('ia_notas') ? <NotasAtendimento isDarkMode={this.state.isDarkMode} /> : up('ia_notas')}></Route>
          </Routes>
          </React.Suspense>
          </LimiteDeCarga>}

        </BrowserRouter>

      </div>
    )
  }

}

export default App;
